import type {
  AIModelListResponse,
  Account,
  BeancountSyncConfig,
  BeancountSyncPlan,
  BeancountSyncStatus,
  Bill,
  BillFilters,
  BillFormData,
  BillPayment,
  BillPaymentFormData,
  BillPaymentStats,
  BillSummary,
  Budget,
  BudgetAnalysis,
  BudgetFilters,
  BudgetFormData,
  BulkUpdateTransactionsData,
  BulkUploadPayslipFormData,
  BulkUploadPayslipResult,
  BulkUploadStatementsFormData,
  BulkUploadStatementsResult,
  Category,
  CreateCategoryDTO,
  CreateWorkflowDTO,
  CreditCardBill,
  CreditCardBillPaymentFormData,
  CreditCardBillUpdateFormData,
  CreditCardBillsMatrix,
  CreditCardOverview,
  CreditCardProfile,
  CreditCardProfileEntry,
  CreditCardProfileFormData,
  CreditCardStatus,
  CreditReport,
  CreditReportFilters,
  CreditReportFormData,
  DashboardSummary,
  FoldMoneyAvailableAccount,
  InboundEmail,
  InboundEmailFilters,
  LoanInterestRateHistory,
  LoanInterestRateHistoryFormData,
  MobileNotification,
  MobileNotificationFilters,
  MonthlySpendingSummary,
  Note,
  Payslip,
  PayslipFilters,
  PayslipFormData,
  Receipt,
  RecentTransaction,
  Statement,
  SyncEntityType,
  Tag,
  Transaction,
  TransactionFilterAI,
  TransactionFilters,
  TransactionFormData,
  TransactionalSms,
  TransactionalSmsFilters,
  DataHealthReport,
  TransferReviewQueueItem,
  TransferReviewStatus,
  UpdateCategoryDTO,
  UpdateWorkflowDTO,
  UploadAndExtractResult,
  UpsertNoteDTO,
  UpsertRunningNoteDTO,
  Workflow,
  WorkflowExecution,
} from "@/types";
import { apiClient as kitApiClient } from "@groot/shell/lib/api";

/**
 * Finance API surface: one typed method per endpoint. Transport is the kit's
 * fetch client (`@groot/shell/lib/api`), which prefixes `/api/v1`, sends the
 * auth cookie, unwraps the `{ success, data }` envelope, rejects with an
 * `ApiError` whose `message` is the server's `error.message` (so toasts and
 * inline errors show the real reason, not a generic status line), and on a
 * mid-session 401 clears auth so <ProtectedRoute> sends the user to /login.
 */
class ApiClient {
  // ─── Transport (delegates to the kit client) ─────────────────────────────
  // Callers receive the inner `data` directly (typed T), never the envelope.
  async get<T>(url: string, params?: Record<string, unknown>): Promise<T> {
    return kitApiClient.get<T>(url, params);
  }

  async post<T>(url: string, data?: unknown): Promise<T> {
    return kitApiClient.post<T>(url, data);
  }

  async put<T>(url: string, data?: unknown): Promise<T> {
    return kitApiClient.put<T>(url, data);
  }

  async patch<T>(url: string, data?: unknown): Promise<T> {
    return kitApiClient.patch<T>(url, data);
  }

  /** DELETE may return 204 (no body) — does not throw on missing data. */
  async delete<T = unknown>(url: string, data?: unknown): Promise<T> {
    return kitApiClient.delete<T>(url, data);
  }

  /** Upload FormData — browser sets the multipart Content-Type + boundary. */
  async postForm<T>(url: string, formData: FormData): Promise<T> {
    return kitApiClient.postForm<T>(url, formData);
  }

  /** Download a binary file as a Blob (no envelope unwrapping). */
  async getBlob(url: string, params?: Record<string, unknown>): Promise<Blob> {
    return kitApiClient.getBlob(url, params);
  }

  /**
   * GET for endpoints that answer 204 when nothing exists: resolves `null` for
   * an empty body, a `null` payload, or any failure instead of throwing.
   */
  private async getOrNull<T>(url: string): Promise<T | null> {
    try {
      return await kitApiClient.get<T>(url);
    } catch {
      return null;
    }
  }

  // Accounts API
  async getAccounts(): Promise<Account[]> {
    return this.get<Account[]>("/accounts");
  }

  /** Live list of bank accounts linked in Fold Money (provider-side IDs). */
  async getFoldMoneyAccounts(phone: string): Promise<FoldMoneyAvailableAccount[]> {
    return this.get<FoldMoneyAvailableAccount[]>("/accounts/bank-sync/fold-money/accounts", {
      phone,
    });
  }

  async getAccount(id: number): Promise<Account> {
    return this.get<Account>(`/accounts/${id}`);
  }

  async bulkUploadPayslips(data: BulkUploadPayslipFormData): Promise<BulkUploadPayslipResult> {
    const formData = new FormData();
    formData.append("company", data.company);

    // Append all PDF files
    for (const pdf of data.pdfs) {
      formData.append("pdfs", pdf);
    }

    return this.postForm<BulkUploadPayslipResult>("/payslips/bulk-upload", formData);
  }

  async createAccount(data: Partial<Account>): Promise<Account> {
    return this.post<Account>("/accounts", data);
  }

  async updateAccount(id: number, data: Partial<Account>): Promise<Account> {
    return this.put<Account>(`/accounts/${id}`, data);
  }

  async deleteAccount(id: number): Promise<void> {
    await this.delete(`/accounts/${id}`);
  }

  // Transactions API
  async getTransactions(
    filters?: TransactionFilters,
  ): Promise<{ transactions: Transaction[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.accountId) params.append("accountId", filters.accountId.toString());
    if (filters?.categoryId) params.append("categoryId", filters.categoryId.toString());
    if (filters?.type) params.append("type", filters.type);
    if (filters?.isReviewed !== undefined)
      params.append("isReviewed", filters.isReviewed.toString());
    if (filters?.minAmount !== undefined) params.append("minAmount", filters.minAmount.toString());
    if (filters?.maxAmount !== undefined) params.append("maxAmount", filters.maxAmount.toString());
    if (filters?.notes) params.append("notes", filters.notes);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.billingCycle) params.append("billingCycle", filters.billingCycle);
    if (filters?.accountType) params.append("accountType", filters.accountType);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);

    const result = await this.get<{ data: Transaction[]; total: number }>(
      `/transactions?${params.toString()}`,
    );
    return { transactions: result.data, total: result.total };
  }

  async getTransaction(id: number): Promise<Transaction> {
    return this.get<Transaction>(`/transactions/${id}`);
  }

  async createTransaction(data: TransactionFormData): Promise<Transaction> {
    return this.post<Transaction>("/transactions", data);
  }

  async updateTransaction(id: number, data: Partial<TransactionFormData>): Promise<Transaction> {
    return this.patch<Transaction>(`/transactions/${id}`, data);
  }

  async deleteTransaction(id: number): Promise<void> {
    await this.delete(`/transactions/${id}`);
  }

  // Transfer detection review queue (issue #213 step 3)
  async getTransferReviewQueue(status?: TransferReviewStatus): Promise<TransferReviewQueueItem[]> {
    const params = new URLSearchParams();
    if (status) params.append("status", status);
    return this.get<TransferReviewQueueItem[]>(
      `/transfer-review-queue${params.size ? `?${params.toString()}` : ""}`,
    );
  }

  async acceptTransferReviewEntry(id: number): Promise<void> {
    await this.post(`/transfer-review-queue/${id}/accept`, {});
  }

  async dismissTransferReviewEntry(id: number, reason: string): Promise<void> {
    await this.post(`/transfer-review-queue/${id}/dismiss`, { reason });
  }

  // Data health report (issue #213 step 4): computed balance checks + lint.
  async getDataHealthReport(): Promise<DataHealthReport> {
    return this.get<DataHealthReport>("/data-health");
  }

  async findDuplicateTransactions(year: number, month?: number): Promise<Transaction[][]> {
    const params = new URLSearchParams();
    params.append("year", year.toString());
    if (month) params.append("month", month.toString());

    const result = await this.get<{
      duplicateGroups: Transaction[][];
      totalDuplicates: number;
    }>(`/transactions/duplicates?${params.toString()}`);
    return result.duplicateGroups;
  }

  async bulkDeleteTransactions(transactionIds: number[]): Promise<void> {
    await this.post<Transaction[]>("/transactions/bulk-delete", {
      transactionIds,
    });
  }

  async bulkUpdateTransactions(
    transactionIds: number[],
    data: BulkUpdateTransactionsData,
  ): Promise<{ updatedCount: number }> {
    return this.post<{ updatedCount: number }>("/transactions/bulk-update", {
      transactionIds,
      data,
    });
  }

  async mergeTransactions(transactionIds: number[]): Promise<Transaction> {
    return this.post<Transaction>("/transactions/merge", { transactionIds });
  }

  async parseTransactionFilterWithAI(query: string): Promise<TransactionFilterAI> {
    return this.post<TransactionFilterAI>("/transactions/ai-filter", { query });
  }

  // Categories API
  async getCategories(): Promise<Category[]> {
    return this.get<Category[]>("/categories");
  }

  async createCategory(data: CreateCategoryDTO): Promise<Category> {
    return this.post<Category>("/categories", data);
  }

  async updateCategory(id: number, data: UpdateCategoryDTO): Promise<Category> {
    return this.put<Category>(`/categories/${id}`, data);
  }

  async deleteCategory(id: number): Promise<Category> {
    return this.delete<Category>(`/categories/${id}`);
  }

  // Tags API
  async getTags(): Promise<Tag[]> {
    return this.get<Tag[]>("/tags");
  }

  // Beancount sync API
  async getBeancountConfig(): Promise<BeancountSyncConfig> {
    return this.get<BeancountSyncConfig>("/beancount/config");
  }

  async updateBeancountConfig(
    patch: Partial<Omit<BeancountSyncConfig, "git">> & {
      git?: { remoteUrl: string; branch: string } | null;
    },
  ): Promise<BeancountSyncConfig> {
    return this.put<BeancountSyncConfig>("/beancount/config", patch);
  }

  async getBeancountStatus(): Promise<BeancountSyncStatus> {
    return this.get<BeancountSyncStatus>("/beancount/status");
  }

  async previewBeancountSync(entityTypes?: SyncEntityType[]): Promise<BeancountSyncPlan> {
    return this.post<BeancountSyncPlan>("/beancount/preview", {
      dryRun: true,
      entityTypes,
    });
  }

  async runBeancountSync(
    entityTypes?: SyncEntityType[],
  ): Promise<
    | BeancountSyncPlan
    | { queued: true; jobId: string | null }
    | { queued: false; alreadyRunning: true; jobId: string }
  > {
    return this.post<
      | BeancountSyncPlan
      | { queued: true; jobId: string | null }
      | { queued: false; alreadyRunning: true; jobId: string }
    >("/beancount/sync", { dryRun: false, entityTypes });
  }

  async resetBeancountState(): Promise<{ deleted: number }> {
    return this.delete<{ deleted: number }>("/beancount/state");
  }

  async createTag(data: { name: string }): Promise<Tag> {
    return this.post<Tag>("/tags", data);
  }

  // Dashboard API
  async getDashboardSummary(): Promise<DashboardSummary> {
    return this.get<DashboardSummary>("/dashboard/summary");
  }

  async getMonthlySpendingSummary(month?: string, year?: number): Promise<MonthlySpendingSummary> {
    const params = new URLSearchParams();
    if (month) params.append("month", month);
    if (year) params.append("year", year.toString());

    const url = `/dashboard/transactions/monthly-summary${params.toString() ? `?${params.toString()}` : ""}`;
    return this.get<MonthlySpendingSummary>(url);
  }

  async getRecentTransactions(options?: {
    limit?: number;
    days?: number;
    accountId?: number;
    isReviewed?: boolean;
  }): Promise<RecentTransaction[]> {
    const params = new URLSearchParams();
    if (options?.limit) params.append("limit", options.limit.toString());
    if (options?.days) params.append("days", options.days.toString());
    if (options?.accountId) params.append("accountId", options.accountId.toString());
    if (options?.isReviewed !== undefined)
      params.append("isReviewed", options.isReviewed.toString());

    const url = `/dashboard/transactions/recent${params.toString() ? `?${params.toString()}` : ""}`;
    return this.get<RecentTransaction[]>(url);
  }

  // AI Models API
  async getAIModels(): Promise<AIModelListResponse> {
    return this.get<AIModelListResponse>("/ai/models");
  }

  // Transactional SMS API
  async getTransactionalSms(
    filters?: TransactionalSmsFilters,
  ): Promise<{ smsList: TransactionalSms[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.from) params.append("from", filters.from);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.fromDate) params.append("fromDate", filters.fromDate);
    if (filters?.toDate) params.append("toDate", filters.toDate);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const result = await this.get<{ data: TransactionalSms[]; total: number }>(
      `/transactional-sms?${params.toString()}`,
    );
    return { smsList: result.data, total: result.total };
  }

  async getTransactionalSmsById(id: number): Promise<TransactionalSms> {
    return this.get<TransactionalSms>(`/transactional-sms/${id}`);
  }

  async getTransactionalSmsSenders(): Promise<string[]> {
    return this.get<string[]>("/transactional-sms/senders");
  }

  async deleteTransactionalSms(id: number): Promise<void> {
    await this.delete(`/transactional-sms/${id}`);
  }

  // Mobile Notifications API
  async getMobileNotifications(
    filters?: MobileNotificationFilters,
  ): Promise<{ notifications: MobileNotification[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.appPackage) params.append("appPackage", filters.appPackage);
    if (filters?.device) params.append("device", filters.device);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.fromDate) params.append("fromDate", filters.fromDate);
    if (filters?.toDate) params.append("toDate", filters.toDate);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const result = await this.get<{
      data: MobileNotification[];
      total: number;
    }>(`/mobile-notifications?${params.toString()}`);
    return { notifications: result.data, total: result.total };
  }

  async getMobileNotificationById(id: number): Promise<MobileNotification> {
    return this.get<MobileNotification>(`/mobile-notifications/${id}`);
  }

  async getMobileNotificationAppPackages(): Promise<
    Array<{ package: string; name: string | null }>
  > {
    return this.get<Array<{ package: string; name: string | null }>>(
      "/mobile-notifications/app-packages",
    );
  }

  // Inbound Emails API
  async getInboundEmails(
    filters?: InboundEmailFilters,
  ): Promise<{ emails: InboundEmail[]; total: number }> {
    const params = new URLSearchParams();
    if (filters?.from) params.append("from", filters.from);
    if (filters?.subject) params.append("subject", filters.subject);
    if (filters?.search) params.append("search", filters.search);
    if (filters?.fromDate) params.append("fromDate", filters.fromDate);
    if (filters?.toDate) params.append("toDate", filters.toDate);
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const result = await this.get<{ data: InboundEmail[]; total: number }>(
      `/inbound-emails?${params.toString()}`,
    );
    return { emails: result.data, total: result.total };
  }

  async getInboundEmailById(id: number): Promise<InboundEmail> {
    return this.get<InboundEmail>(`/inbound-emails/${id}`);
  }

  async getInboundEmailSenders(): Promise<string[]> {
    return this.get<string[]>("/inbound-emails/senders");
  }

  async deleteInboundEmail(id: number): Promise<void> {
    await this.delete(`/inbound-emails/${id}`);
  }

  async getMobileNotificationDevices(): Promise<string[]> {
    return this.get<string[]>("/mobile-notifications/devices");
  }

  async deleteMobileNotification(id: number): Promise<void> {
    await this.delete(`/mobile-notifications/${id}`);
  }

  // Budgets API
  async getBudgets(filters?: BudgetFilters): Promise<{ budgets: Budget[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.categoryId) params.append("categoryId", filters.categoryId.toString());
    if (filters?.month) params.append("month", filters.month.toString());
    if (filters?.startMonth) params.append("startMonth", filters.startMonth.toString());
    if (filters?.endMonth) params.append("endMonth", filters.endMonth.toString());
    if (filters?.includeDeleted !== undefined)
      params.append("includeDeleted", filters.includeDeleted.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());

    const result = await this.get<{ data: Budget[]; total: number }>(
      `/budgets?${params.toString()}`,
    );
    return { budgets: result.data, total: result.total };
  }

  async getBudget(id: number): Promise<Budget> {
    return this.get<Budget>(`/budgets/${id}`);
  }

  async getBudgetsByMonth(month: string): Promise<Budget[]> {
    return this.get<Budget[]>(`/budgets/month/${month}`);
  }

  async createBudget(data: BudgetFormData): Promise<Budget> {
    return this.post<Budget>("/budgets", data);
  }

  async updateBudget(id: number, data: Partial<BudgetFormData>): Promise<Budget> {
    return this.patch<Budget>(`/budgets/${id}`, data);
  }

  async upsertBudget(data: BudgetFormData): Promise<Budget> {
    return this.post<Budget>("/budgets/upsert", data);
  }

  async deleteBudget(id: number): Promise<void> {
    await this.delete(`/budgets/${id}`);
  }

  async copyBudgets(sourceMonth: string, targetMonth: string): Promise<Budget[]> {
    return this.post<Budget[]>("/budgets/copy", { sourceMonth, targetMonth });
  }

  async getBudgetAnalysis(
    month?: string,
    includeExcludedFromBudget?: boolean,
  ): Promise<BudgetAnalysis> {
    const queryParams = new URLSearchParams();
    if (month) queryParams.append("month", month);
    if (includeExcludedFromBudget !== undefined) {
      queryParams.append("includeExcludedFromBudget", String(includeExcludedFromBudget));
    }
    const params = queryParams.toString() ? `?${queryParams.toString()}` : "";
    return this.get<BudgetAnalysis>(`/budgets/analysis${params}`);
  }

  async getMultiMonthBudgetAnalysis(months: string[]): Promise<BudgetAnalysis[]> {
    const monthsParam = months.join(",");
    return this.get<BudgetAnalysis[]>(`/budgets/analysis/multi?months=${monthsParam}`);
  }

  // Credit Cards API
  async getCreditCardOverview(
    month?: string,
    status: CreditCardStatus = "active",
  ): Promise<CreditCardOverview> {
    const params = new URLSearchParams({ status });
    if (month) params.set("month", month);
    return this.get<CreditCardOverview>(`/credit-cards/overview?${params.toString()}`);
  }

  async getCreditCardBills(
    startMonth: string,
    endMonth: string,
    status: CreditCardStatus = "active",
  ): Promise<CreditCardBillsMatrix> {
    return this.get<CreditCardBillsMatrix>(
      `/credit-cards/bills?startMonth=${startMonth}&endMonth=${endMonth}&status=${status}`,
    );
  }

  async getCreditCardProfiles(
    status: CreditCardStatus = "active",
  ): Promise<CreditCardProfileEntry[]> {
    return this.get<CreditCardProfileEntry[]>(`/credit-cards/profiles?status=${status}`);
  }

  async upsertCreditCardProfile(
    accountId: number,
    data: CreditCardProfileFormData,
  ): Promise<CreditCardProfile> {
    return this.put<CreditCardProfile>(`/credit-cards/profiles/${accountId}`, data);
  }

  async upsertCreditCardBill(
    accountId: number,
    month: string,
    data: Partial<CreditCardBillUpdateFormData>,
  ): Promise<CreditCardBill> {
    return this.put<CreditCardBill>(`/credit-cards/bills/${accountId}/${month}`, data);
  }

  async recordCreditCardBillPayment(
    billId: number,
    data: CreditCardBillPaymentFormData,
  ): Promise<CreditCardBill> {
    return this.post<CreditCardBill>(`/credit-cards/bills/${billId}/pay`, data);
  }

  // Notes API
  // The server returns 204 when no note exists for the month — resolve null.
  async getNoteByMonth(month: string): Promise<Note | null> {
    return this.getOrNull<Note>(`/notes?month=${month}`);
  }

  async upsertNote(data: UpsertNoteDTO): Promise<Note> {
    return this.post<Note>("/notes", data);
  }

  async getAllNotes(): Promise<Note[]> {
    return this.get<Note[]>("/notes/all");
  }

  async deleteNote(month: string): Promise<void> {
    await this.delete(`/notes/${month}`);
  }

  // Running Notes API
  // The server 204s when the slug is unknown — resolve null.
  async getRunningNote(slug: string): Promise<Note | null> {
    return this.getOrNull<Note>(`/notes/running/${slug}`);
  }

  async upsertRunningNote(data: UpsertRunningNoteDTO): Promise<Note> {
    return this.post<Note>("/notes/running", data);
  }

  async getAllRunningNotes(): Promise<Note[]> {
    return this.get<Note[]>("/notes/running");
  }

  async deleteRunningNote(slug: string): Promise<void> {
    await this.delete(`/notes/running/${slug}`);
  }

  // Bills API
  async getBills(filters?: BillFilters): Promise<{ bills: Bill[]; total: number }> {
    const params = new URLSearchParams();

    if (filters?.isActive !== undefined) params.append("isActive", filters.isActive.toString());
    if (filters?.trackingMode) params.append("trackingMode", filters.trackingMode);
    if (filters?.billingCycleType) params.append("billingCycleType", filters.billingCycleType);
    if (filters?.upcomingDays) params.append("upcomingDays", filters.upcomingDays.toString());
    if (filters?.search) params.append("search", filters.search);
    if (filters?.includeDeleted !== undefined)
      params.append("includeDeleted", filters.includeDeleted.toString());
    if (filters?.limit) params.append("limit", filters.limit.toString());
    if (filters?.offset) params.append("offset", filters.offset.toString());
    if (filters?.sortBy) params.append("sortBy", filters.sortBy);
    if (filters?.sortOrder) params.append("sortOrder", filters.sortOrder);

    const result = await this.get<{ data: Bill[]; total: number }>(`/bills?${params.toString()}`);
    return { bills: result.data, total: result.total };
  }

  async getBill(id: number): Promise<Bill> {
    return this.get<Bill>(`/bills/${id}`);
  }

  async createBill(data: BillFormData): Promise<Bill> {
    return this.post<Bill>("/bills", data);
  }

  async updateBill(id: number, data: Partial<BillFormData>): Promise<Bill> {
    return this.patch<Bill>(`/bills/${id}`, data);
  }

  async deleteBill(id: number): Promise<void> {
    await this.delete(`/bills/${id}`);
  }

  async markBillAsPaid(
    id: number,
    data?: {
      paidDate?: string;
      amount?: number;
      accountId?: number;
      notes?: string;
    },
  ): Promise<{ bill: Bill; payment: unknown }> {
    return this.post<{ bill: Bill; payment: unknown }>(`/bills/${id}/paid`, data || {});
  }

  async getBillSummary(): Promise<BillSummary> {
    return this.get<BillSummary>("/bills/summary");
  }

  // Bill Payment Methods
  async getPaymentHistoryForBill(billId: number): Promise<BillPayment[]> {
    return this.get<BillPayment[]>(`/bills/${billId}/payments`);
  }

  async updateBillPayment(id: number, data: Partial<BillPaymentFormData>): Promise<BillPayment> {
    return this.put<BillPayment>(`/bills/payments/${id}`, data);
  }

  async deleteBillPayment(id: number): Promise<void> {
    await this.delete(`/bills/payments/${id}`);
  }

  async getBillPaymentStats(billId: number): Promise<BillPaymentStats> {
    return this.get<BillPaymentStats>(`/bills/${billId}/payments/stats`);
  }

  async getBillCalendarLink(): Promise<{ url: string; token: string }> {
    return this.get<{ url: string; token: string }>("/bills/calendar/link");
  }

  // Storage API
  async downloadFile(filePath: string): Promise<Blob> {
    return this.getBlob("/storage/files/download", { filePath });
  }

  // Statements API (for extraction workflow - legacy methods)
  async uploadStatement(formData: FormData): Promise<unknown> {
    return this.postForm<unknown>("/statements/upload", formData);
  }

  async getStatements(): Promise<Statement[]> {
    return this.get<Statement[]>("/statements");
  }

  async updateStatementExtractedData(id: number, extractedData: unknown): Promise<unknown> {
    return this.put<unknown>(`/statements/${id}/extracted-data`, {
      extractedData,
    });
  }

  async importStatementTransactions(
    id: number,
    data: { transactions: unknown[]; accountId: number },
  ): Promise<unknown> {
    return this.post<unknown>(`/statements/${id}/import`, data);
  }

  async submitStatementSelections(id: number, data: { selections: unknown[] }): Promise<unknown> {
    return this.post<unknown>(`/statements/${id}/selections`, data);
  }

  // Payslip API
  async getPayslips(filters?: PayslipFilters): Promise<Payslip[]> {
    const params = new URLSearchParams();

    if (filters?.company) params.append("company", filters.company);
    if (filters?.month) params.append("month", filters.month);
    if (filters?.startMonth) params.append("startMonth", filters.startMonth);
    if (filters?.endMonth) params.append("endMonth", filters.endMonth);
    if (filters?.includeDetails !== undefined)
      params.append("includeDetails", filters.includeDetails.toString());
    if (filters?.includeDeleted !== undefined)
      params.append("includeDeleted", filters.includeDeleted.toString());

    return this.get<Payslip[]>(`/payslips?${params.toString()}`);
  }

  async getPayslip(id: number, includeDetails = true): Promise<Payslip> {
    const params = new URLSearchParams();
    params.append("includeDetails", includeDetails.toString());

    return this.get<Payslip>(`/payslips/${id}?${params.toString()}`);
  }

  async uploadPayslip(data: PayslipFormData): Promise<Payslip> {
    const formData = new FormData();
    formData.append("pdf", data.pdf);
    formData.append("company", data.company);
    formData.append("month", data.month);
    if (data.grossEarnings !== undefined)
      formData.append("grossEarnings", data.grossEarnings.toString());
    if (data.grossDeductions !== undefined)
      formData.append("grossDeductions", data.grossDeductions.toString());
    if (data.netPay !== undefined) formData.append("netPay", data.netPay.toString());
    if (data.notes) formData.append("notes", data.notes);

    // Add earnings as JSON if provided
    if (data.earnings && data.earnings.length > 0) {
      formData.append("earnings", JSON.stringify(data.earnings));
    }

    // Add deductions as JSON if provided
    if (data.deductions && data.deductions.length > 0) {
      formData.append("deductions", JSON.stringify(data.deductions));
    }

    return this.postForm<Payslip>("/payslips", formData);
  }

  async deletePayslip(id: number): Promise<void> {
    await this.delete(`/payslips/${id}`);
  }

  getPayslipPdfUrl(id: number): string {
    return `/api/v1/payslips/${id}/pdf`;
  }

  async getPayslipCompanies(): Promise<string[]> {
    return this.get<string[]>("/payslips/companies");
  }

  // Statement API (unified for both extraction and archival)
  async getStatementsByAccountId(accountId: number, includeDeleted = false): Promise<Statement[]> {
    const params = new URLSearchParams();
    params.append("includeDeleted", includeDeleted.toString());

    return this.get<Statement[]>(`/statements/account/${accountId}?${params.toString()}`);
  }

  async getStatement(id: number): Promise<Statement> {
    return this.get<Statement>(`/statements/${id}`);
  }

  async extractStatement(
    id: number,
    options?: {
      autoUpload?: boolean;
      accountId?: number;
      ignorePagesFromEnd?: number;
      aiModel?: string;
      renameFile?: boolean;
    },
  ): Promise<{ id: number; jobId: string | null }> {
    return this.post<{ id: number; jobId: string | null }>(
      `/statements/${id}/extract`,
      options || {},
    );
  }

  async bulkUploadStatements(
    data: BulkUploadStatementsFormData,
  ): Promise<BulkUploadStatementsResult> {
    const formData = new FormData();
    formData.append("accountId", data.accountId.toString());
    if (data.notes) formData.append("notes", data.notes);

    // Append all PDF files
    for (const pdf of data.pdfs) {
      formData.append("pdfs", pdf);
    }

    return this.postForm<BulkUploadStatementsResult>("/statements/bulk-upload", formData);
  }

  async deleteStatement(id: number): Promise<void> {
    await this.delete(`/statements/${id}`);
  }

  getStatementPdfUrl(id: number): string {
    return `/api/v1/statements/${id}/pdf`;
  }

  // Credit Report API
  async getCreditReports(filters?: CreditReportFilters): Promise<CreditReport[]> {
    const params = new URLSearchParams();

    if (filters?.bureau) params.append("bureau", filters.bureau);
    if (filters?.startDate) params.append("startDate", filters.startDate);
    if (filters?.endDate) params.append("endDate", filters.endDate);
    if (filters?.includeDeleted !== undefined)
      params.append("includeDeleted", filters.includeDeleted.toString());

    return this.get<CreditReport[]>(`/credit-reports?${params.toString()}`);
  }

  async getCreditReport(id: number): Promise<CreditReport> {
    return this.get<CreditReport>(`/credit-reports/${id}`);
  }

  async uploadCreditReport(data: CreditReportFormData): Promise<CreditReport> {
    const formData = new FormData();
    if (data.pdf) {
      formData.append("pdf", data.pdf);
    }
    formData.append("bureau", data.bureau);
    formData.append("reportDate", data.reportDate);
    if (data.creditScore !== undefined) formData.append("creditScore", data.creditScore.toString());
    if (data.notes) formData.append("notes", data.notes);

    return this.postForm<CreditReport>("/credit-reports", formData);
  }

  async deleteCreditReport(id: number): Promise<void> {
    await this.delete(`/credit-reports/${id}`);
  }

  getCreditReportPdfUrl(id: number): string {
    return `/api/v1/credit-reports/${id}/pdf`;
  }

  async getCreditScoreTrend(): Promise<CreditReport[]> {
    return this.get<CreditReport[]>("/credit-reports/trend");
  }

  // Workflows API
  async getWorkflows(): Promise<Workflow[]> {
    return this.get<Workflow[]>("/workflows?includeDisabled=true");
  }

  async getWorkflow(id: number): Promise<Workflow> {
    return this.get<Workflow>(`/workflows/${id}`);
  }

  async createWorkflow(data: CreateWorkflowDTO): Promise<Workflow> {
    return this.post<Workflow>("/workflows", data);
  }

  async updateWorkflow(id: number, data: UpdateWorkflowDTO): Promise<Workflow> {
    return this.put<Workflow>(`/workflows/${id}`, data);
  }

  async deleteWorkflow(id: number): Promise<void> {
    await this.delete(`/workflows/${id}`);
  }

  async duplicateWorkflow(id: number): Promise<Workflow> {
    return this.post<Workflow>(`/workflows/${id}/duplicate`);
  }

  async executeWorkflow(id: number, context: Record<string, unknown>): Promise<WorkflowExecution> {
    return this.post<WorkflowExecution>(`/workflows/${id}/execute`, {
      context,
    });
  }

  async getWorkflowExecutions(workflowId: number): Promise<WorkflowExecution[]> {
    return this.get<WorkflowExecution[]>(`/workflows/${workflowId}/executions`);
  }

  async getWorkflowExecution(workflowId: number, executionId: number): Promise<WorkflowExecution> {
    return this.get<WorkflowExecution>(`/workflows/${workflowId}/executions/${executionId}`);
  }

  // Receipt API methods

  async uploadAndExtractReceipt(file: File, notes?: string): Promise<UploadAndExtractResult> {
    const formData = new FormData();
    formData.append("file", file);
    if (notes) {
      formData.append("notes", notes);
    }
    return this.postForm<UploadAndExtractResult>("/receipts/extract", formData);
  }

  async getReceipts(): Promise<Receipt[]> {
    return this.get<Receipt[]>("/receipts");
  }

  async getReceipt(id: number): Promise<Receipt> {
    return this.get<Receipt>(`/receipts/${id}`);
  }

  async deleteReceipt(id: number): Promise<void> {
    await this.delete(`/receipts/${id}`);
  }

  getReceiptPdfUrl(id: number): string {
    return `/api/v1/receipts/${id}/pdf`;
  }

  // Loan Interest Rate History API
  async getInterestRateHistory(accountId: number): Promise<LoanInterestRateHistory[]> {
    return this.get<LoanInterestRateHistory[]>(`/accounts/${accountId}/interest-rate-history`);
  }

  async createInterestRateEntry(
    accountId: number,
    data: LoanInterestRateHistoryFormData,
  ): Promise<LoanInterestRateHistory> {
    return this.post<LoanInterestRateHistory>(`/accounts/${accountId}/interest-rate-history`, data);
  }

  async updateInterestRateEntry(
    accountId: number,
    id: number,
    data: Partial<LoanInterestRateHistoryFormData>,
  ): Promise<LoanInterestRateHistory> {
    return this.put<LoanInterestRateHistory>(
      `/accounts/${accountId}/interest-rate-history/${id}`,
      data,
    );
  }

  async deleteInterestRateEntry(accountId: number, id: number): Promise<void> {
    await this.delete(`/accounts/${accountId}/interest-rate-history/${id}`);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
